import { useState } from "react";
import { Field, inputClass } from "../routes/SignIn.js";
import { BrandLogo } from "../ui/BrandLogo.js";
import { PrimaryButton, SparkleIcon } from "./ui.js";

/**
 * Step 1 — website submission. The single magical entry point: paste a URL and
 * we build the whole profile. "I don't have a website" drops to a blank profile
 * the pro fills in the same card-review UI.
 */
export function WebsiteStep({
  onSubmit,
  onNoWebsite,
  error,
}: {
  onSubmit: (url: string) => void;
  onNoWebsite: () => void;
  error: string | null;
}) {
  const [url, setUrl] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = url.trim();
    if (trimmed) onSubmit(trimmed);
  }

  return (
    <main className="mx-auto max-w-md px-6 py-12">
      <BrandLogo className="mx-auto" />
      <div className="mt-6 flex justify-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
          <SparkleIcon className="h-7 w-7" />
        </span>
      </div>
      <h1 className="mt-5 text-center text-3xl font-bold text-charcoal">
        Let&rsquo;s build your business profile
      </h1>
      <p className="mt-3 text-center text-base leading-relaxed text-charcoal">
        Drop in your website and we&rsquo;ll set up your pro profile for you — services, service
        area, photos and all. You just review it.
      </p>

      <form className="mt-8 space-y-4" onSubmit={submit} noValidate>
        <Field label="Business website">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="yourbusiness.com"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            className={inputClass}
          />
        </Field>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <PrimaryButton type="submit" disabled={!url.trim()}>
          Build my profile
        </PrimaryButton>
      </form>

      <button
        type="button"
        onClick={onNoWebsite}
        className="mt-5 block w-full text-center text-sm font-medium text-brand-700"
      >
        I don&rsquo;t have a website
      </button>
    </main>
  );
}
